import { Query } from '@apollo/client/react/components';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { DatasourceQuery, DatasourceQueryVariables, DatasourceDocument } from '../../../../../gen-src/queries';
import { ApiRequestsContext, CenteredFadeInSpinner, CenteredMessage } from '../../../data-catalog-shared';
import { RenderQueryResult } from '../renderQueryResult';
import { createRenderDataWithErrorFunction, renderError, renderNoData } from '../widgets/ErrorMessageWidgets';

export type DatasourceQueryWrapperWithContextProps = {
  readonly vizportalUrlId: string;
  readonly children: (result: Unpacked<DatasourceQuery['publishedDatasources']>, canSetDescription: boolean) => React.ReactNode;
};

export type DatasourceQueryWrapperProps = DatasourceQueryWrapperWithContextProps & {
  readonly canSetDescription: boolean;
};

// React Hooks can't be used inside a component class, so this function uses Hooks
// functionality to check permissions.
export function DatasourceQueryWrapperWithContext(props: DatasourceQueryWrapperWithContextProps): JSX.Element {
  const apiRequests = React.useContext(ApiRequestsContext);
  const [canSetDescription, setCanSetDescription] = React.useState(false);

  React.useEffect(
    () => {
      apiRequests.canUserSetFieldDescription({ datasourceVizportalId: props.vizportalUrlId ?? '' })
        .then(r => setCanSetDescription(r))
        .catch(() => { return; });
    },
    [apiRequests, props.vizportalUrlId]
  );

  return <DatasourceQueryWrapper {...props} canSetDescription={canSetDescription} />;
}

class DatasourceQueryWrapper extends React.Component<DatasourceQueryWrapperProps> {

  public override render(): React.ReactNode {
    return (
      <Query<DatasourceQuery, DatasourceQueryVariables>
        query={DatasourceDocument}
        variables={{ vizportalUrlId: this.props.vizportalUrlId }}
      >
        {result => <RenderQueryResult result={result} renderers={{
          renderLoading: () => <CenteredFadeInSpinner />,
          renderData: this.renderDatasourceData,
          renderNoData: renderNoData,
          renderDataWithError: createRenderDataWithErrorFunction(this.renderDatasourceData),
          renderError: renderError,
        }} />}
      </Query>
    );
  }

  private readonly renderDatasourceData = (data: DatasourceQuery): React.ReactNode => {
    const datasource = data.publishedDatasources ? data.publishedDatasources[0] : null;

    if (datasource) {
      return this.props.children(datasource, this.props.canSetDescription);
    } else {
      return <CenteredMessage message={Messages.ErrorItemNotFound()} />;
    }
  }

}
